import { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { useAppUpdate } from '../pwa';

/**
 * Bandeau « Nouvelle version disponible — Recharger ». S'affiche quand
 * `useAppUpdate` détecte une nouvelle version ; le clic déclenche le
 * rechargement nucléaire (purge caches + SW puis reload). `role="status"`
 * pour l'annonce aux lecteurs d'écran.
 */
export function UpdateBanner(): React.JSX.Element | null {
  const { t } = useI18n();
  const { updateAvailable, reload } = useAppUpdate();
  const [reloading, setReloading] = useState(false);

  if (!updateAvailable) return null;

  const onReload = (): void => {
    setReloading(true);
    void reload();
  };

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-center gap-3 bg-warning px-4 py-2 text-center text-sm font-medium text-warning-fg"
    >
      <span>{t('pwa.updateAvailable')}</span>
      <button
        type="button"
        onClick={onReload}
        disabled={reloading}
        className="inline-flex min-h-11 items-center gap-1.5 rounded-md bg-warning-fg/15 px-3 py-1 font-semibold underline-offset-2 hover:bg-warning-fg/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warning-fg disabled:opacity-70 motion-reduce:transition-none"
      >
        <ArrowPathIcon
          className={reloading ? 'h-5 w-5 animate-spin motion-reduce:animate-none' : 'h-5 w-5'}
          aria-hidden="true"
        />
        {reloading ? t('pwa.reloading') : t('pwa.reload')}
      </button>
    </div>
  );
}
